import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	SxProps,
	Typography,
} from '@mui/material'
import React, { useRef, useState } from 'react'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import { ImageSlider } from './ImageSlider'
import { getBlobs } from '../../services/CommonServices/imageAttachmentService'
import { AttachmentsUploadResponse } from '../../types/Upload/AttachmentResponse'

interface ImageAttachmentDialogProps {
	onSave: (blobs: Blob[]) => Promise<void>
	getAttachments: () => Promise<AttachmentsUploadResponse[]> | null
	deleteAttachment: (attachmentId: number) => Promise<void> | null
	readOnly?: boolean
	title: string
	iconStyles?: SxProps
}

interface AttachmentInfo {
	isExisting: boolean
	existingAttachment?: AttachmentsUploadResponse
}

export function ImageAttachmentDialog({
	onSave,
	getAttachments,
	deleteAttachment,
	readOnly,
	title,
	iconStyles,
}: ImageAttachmentDialogProps) {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [submiting, setSubmiting] = useState(false)
	const [blobs, setBlobs] = useState<Blob[]>([])
	const [attachmentInfo, setAttachmentInfo] = useState<AttachmentInfo[]>([])
	const cameraInputRef = useRef<HTMLInputElement>(null)
	const galleryInputRef = useRef<HTMLInputElement>(null)

	const handleOpen = async () => {
		setOpen(true)
		setLoading(true)
		const existingAttachments = await getAttachments()
		if (existingAttachments) {
			const existingBlobs = await getBlobs(existingAttachments)
			setBlobs(existingBlobs)
			setAttachmentInfo(
				existingAttachments.map((attachment) => ({
					isExisting: true,
					existingAttachment: attachment,
				})),
			)
		}
		setLoading(false)
	}

	const handleClose = () => {
		setOpen(false)
		setBlobs([])
		setAttachmentInfo([])
	}

	const handleSubmit = async () => {
		setSubmiting(true)
		// Only pass the non-existing blobs to onSave
		const newBlobs = blobs.filter((_, index) => !attachmentInfo[index].isExisting)
		await onSave(newBlobs)
		setSubmiting(false)
		setOpen(false)
	}

	const handleOpenCamera = () => {
		if (cameraInputRef.current) {
			cameraInputRef.current.value = ''
			cameraInputRef.current.click()
		}
	}

	const handleCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file?.type.startsWith('image/')) {
			setBlobs((prev) => [...prev, file])
			setAttachmentInfo((prev) => [...prev, { isExisting: false }])
		}
		if (cameraInputRef.current) {
			cameraInputRef.current.value = ''
		}
	}

	const handleOpenGallery = () => {
		galleryInputRef.current?.click()
	}

	const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files
		if (files?.length) {
			const newBlobs = Array.from(files).filter((file) => file.type.startsWith('image/'))
			setBlobs((prev) => [...prev, ...newBlobs])
			setAttachmentInfo((prev) => [...prev, ...newBlobs.map(() => ({ isExisting: false }))])
		}
		if (galleryInputRef.current) {
			galleryInputRef.current.value = ''
		}
	}

	const handleRemoveAttachment = async (index: number) => {
		setLoading(true)

		if (attachmentInfo[index].isExisting && deleteAttachment) {
			await deleteAttachment(attachmentInfo[index].existingAttachment!.id)
		}
		setBlobs((prev) => prev.filter((_, i) => i !== index))
		setAttachmentInfo((prev) => prev.filter((_, i) => i !== index))
		setLoading(false)
		if (blobs.length === 1) {
			setOpen(false)
		}
	}

	return (
		<>
			<input
				ref={cameraInputRef}
				type='file'
				accept='image/*'
				capture='environment'
				style={{ display: 'none' }}
				onChange={handleCameraChange}
			/>
			<input
				ref={galleryInputRef}
				type='file'
				accept='image/*'
				multiple
				style={{ display: 'none' }}
				onChange={handleGalleryChange}
			/>
			<IconButton
				size='medium'
				onClick={handleOpen}
				sx={{
					color: 'primary.main',
					opacity: 0.7,
					'&:hover': {
						opacity: 1,
					},
					...iconStyles,
				}}
			>
				<CameraAltIcon fontSize='medium' />
			</IconButton>
			<Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent
					sx={{
						overflow: 'auto',
						maxHeight: '70vh',
						display: 'flex',
						flexDirection: 'column',
						'&:first-of-type': { pt: 2 },
					}}
				>
					{loading ? (
						<CircularProgress />
					) : blobs.length !== 0 ? (
						<Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
							<ImageSlider
								blobs={blobs}
								setBlobs={setBlobs}
								handleDelete={handleRemoveAttachment}
								readOnly={readOnly}
							/>
						</Box>
					) : (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								minHeight: 200,
								border: '2px dashed',
								borderColor: 'divider',
								borderRadius: 2,
								bgcolor: 'action.hover',
								py: 4,
								px: 3,
							}}
						>
							<AddPhotoAlternateOutlinedIcon
								sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }}
							/>
							<Typography variant="h6" color="text.secondary" gutterBottom>
								No images yet
							</Typography>
							<Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 280 }}>
								Use Open Camera to take a photo, or Choose from gallery to add existing images.
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', width: '100%' }}>
					{submiting ? (
						<CircularProgress />
					) : (
						<>
							<div style={{ display: 'flex', gap: 8 }}>
								{!readOnly && (
									<>
										<Button
											variant='contained'
											color='primary'
											onClick={handleOpenCamera}
											startIcon={<CameraAltIcon />}
										>
											Open Camera
										</Button>
										<Button
											variant='outlined'
											color='primary'
											onClick={handleOpenGallery}
											startIcon={<PhotoLibraryIcon />}
											sx={{ color: 'text.primary' }}
										>
											Choose from gallery
										</Button>
									</>
								)}
							</div>
							<div style={{ display: 'flex', gap: 8 }}>
								<Button variant='contained' color='warning' onClick={handleClose}>
									Cancel
								</Button>
								{!readOnly && blobs.length > 0 && (
									<Button variant='contained' color='success' onClick={handleSubmit}>
										Submit
									</Button>
								)}
							</div>
						</>
					)}
				</DialogActions>
			</Dialog>
		</>
	)
}
